import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Tag,
  HStack,
  VStack,
  RadioGroup,
  Radio,
  Button,
  Spinner,
  Badge,
  useColorModeValue,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { Rock, User, Milestone, Issue, ActionItem } from '../../types';
import { CheckCircle, Plus } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { getComputedRockStatus } from '../../Rocks/rocks.util';
import { useMemo } from 'react';

interface RockReviewCardProps {
  rock: Rock;
  owner?: User;
  onStatusChange: (rockId: string, newStatus: 'on-track' | 'off-track' | 'at-risk') => void;
  loadMilestonesForRock: (rockId: string) => Promise<Milestone[]>;
  loadIssuesForRock: (rockId: string) => Promise<Issue[]>;
  loadActionItemsForRock?: (rockId: string) => Promise<ActionItem[]>;
  toggleMilestone: (milestoneId: string, complete: boolean) => Promise<void>;
  onIssueClick?: (rockId: string) => void;
  onResolveIssue?: (issueId: string, issueName: string) => void;
  onAddIssue?: (rockId: string) => void;
  isPresenter?: boolean;
  refreshTrigger?: number; // When this changes, refetch data
}

export function RockReviewCard({
  rock,
  owner,
  onStatusChange,
  loadMilestonesForRock,
  loadIssuesForRock,
  loadActionItemsForRock,
  toggleMilestone,
  onIssueClick,
  onResolveIssue,
  onAddIssue,
  isPresenter = true,
  refreshTrigger,
}: RockReviewCardProps) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getQuarter = (date: Date | string) => {
    const d = new Date(date);
    const month = d.getMonth();
    return `Q${Math.floor(month / 3) + 1} ${d.getFullYear()}`;
  };

  const fetchDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const promises: Promise<any>[] = [
        loadMilestonesForRock(rock.id),
        loadIssuesForRock(rock.id),
      ];
      
      if (loadActionItemsForRock) {
        promises.push(loadActionItemsForRock(rock.id));
      }
      
      const results = await Promise.all(promises);
      const milestonesData = results[0] as Milestone[];
      const issuesData = results[1] as Issue[];
      const actionItemsData = results[2] as ActionItem[] || [];
      
      setMilestones(
        milestonesData.sort(
          (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        )
      );
      setIssues(issuesData);
      setActionItems(actionItemsData);
    } catch (error) {
      console.error('Failed to load rock details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load rock details. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [rock.id, loadMilestonesForRock, loadIssuesForRock, loadActionItemsForRock, toast]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails, refreshTrigger]); // Add refreshTrigger as dependency

  const handleToggleMilestone = async (milestone: Milestone) => {
    try {
      await toggleMilestone(milestone.id, !milestone.complete);
      // Re-fetch milestones to show the change
      const milestonesData = await loadMilestonesForRock(rock.id);
      setMilestones(
        milestonesData.sort(
          (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        )
      );
    } catch (error) {
      console.error('Failed to toggle milestone:', error);
      toast({
        title: 'Error',
        description: 'Failed to toggle milestone. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return `(${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
  };

  const openMilestones = milestones.filter((m) => !m.complete);
  const completedMilestones = milestones.filter((m) => m.complete);
  // Filter issues: open/in-progress vs solved
  const openIssues = useMemo(() => issues.filter(issue => issue.status === 'open' || issue.status === 'in-progress'), [issues]);
  const resolvedIssues = useMemo(() => issues.filter(issue => issue.status === 'solved'), [issues]);
  const openActionItems = actionItems.filter((t) => !t.complete);
  const completedActionItems = actionItems.filter((t) => t.complete);
  
  // Compute rock status from open issues count
  // Rock with any open issues = off-track, Rock with no open issues = on-track
  // Note: at-risk status is not computed, use rock.status for that
  const computedStatus = useMemo(() => {
    // For on-track/off-track, compute from issues
    // For at-risk, use stored status
    if (rock.status === 'at-risk') {
      return rock.status;
    }
    return getComputedRockStatus(rock, openIssues);
  }, [rock, openIssues]);

  return (
    <Box
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      shadow="sm"
      bg={bgColor}
      p={4}
    >
      <VStack align="stretch" spacing={3}>
        {/* Top Row: Title, Owner, Toggles, Badges */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3} flex={1} minW="200px">
            <Heading size="sm">{rock.rock}</Heading>
            {owner && (
              <Tag colorScheme="green" size="sm">
                {owner.name}
              </Tag>
            )}
          </HStack>

          <HStack spacing={4} flexWrap="wrap" justify="flex-end">
            {isPresenter && (
              <RadioGroup
                onChange={(value) =>
                  onStatusChange(rock.id, value as 'on-track' | 'off-track' | 'at-risk')
                }
                value={computedStatus === 'at-risk' ? rock.status : computedStatus}
                size="sm"
              >
                <HStack spacing={3}>
                  <Radio value="on-track">On Track</Radio>
                  <Radio value="off-track">Off Track</Radio>
                </HStack>
              </RadioGroup>
            )}
            {!isPresenter && (
              <HStack spacing={3}>
                <Radio isChecked={computedStatus === 'on-track'} isDisabled>On Track</Radio>
                <Radio isChecked={computedStatus === 'off-track'} isDisabled>Off Track</Radio>
              </HStack>
            )}

            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
              {getQuarter(rock.dueDate)}
            </Text>

            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
              {openIssues.length} {openIssues.length === 1 ? 'Issue' : 'Issues'}
            </Text>

            <Badge
              colorScheme={
                computedStatus === 'on-track'
                  ? 'green'
                  : computedStatus === 'at-risk'
                  ? 'yellow'
                  : 'red'
              }
              variant="solid"
              fontSize="0.8em"
              px={2}
              py={1}
            >
              {computedStatus === 'on-track'
                ? 'ON TRACK'
                : computedStatus === 'at-risk'
                ? 'AT RISK'
                : 'OFF TRACK'}
            </Badge>
          </HStack>
        </Flex>

        {/* Details Section */}
        {isLoading ? (
          <Box textAlign="center" py={4}>
            <Spinner size="sm" />
          </Box>
        ) : (
          <Box pl={4} borderTop="1px" borderColor={borderColor} pt={3}>
            <VStack align="stretch" spacing={4}>
              {/* OPEN MILESTONES */}
              {openMilestones.length > 0 && (
                <Box>
                  <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    OPEN MILESTONES:
                  </Heading>
                  <VStack align="stretch" spacing={2}>
                    {openMilestones.map((milestone) => (
                      <HStack key={milestone.id} spacing={2}>
                        <Radio isChecked={false} isDisabled={!isPresenter} />
                        <Text fontSize="sm">
                          {milestone.task} {formatDate(milestone.dueDate)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* COMPLETED MILESTONES */}
              {completedMilestones.length > 0 && (
                <Box>
                  <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    COMPLETED MILESTONES:
                  </Heading>
                  <VStack align="stretch" spacing={2}>
                    {completedMilestones.map((milestone) => (
                      <HStack key={milestone.id} spacing={2}>
                        <Icon as={CheckCircle} w={4} h={4} color="green.500" />
                        <Text fontSize="sm" color="green.600" textDecoration="line-through">
                          {milestone.task} {formatDate(milestone.dueDate)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* OPEN ACTION ITEMS */}
              {openActionItems.length > 0 && (
                <Box>
                  <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    OPEN ACTION ITEMS:
                  </Heading>
                  <VStack align="stretch" spacing={2}>
                    {openActionItems.map((actionItem) => (
                      <HStack key={actionItem.id} spacing={2}>
                        <Radio isChecked={false} isDisabled={!isPresenter} />
                        <Text fontSize="sm">
                          {actionItem.description} {actionItem.dueDate && formatDate(actionItem.dueDate)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* COMPLETED ACTION ITEMS */}
              {completedActionItems.length > 0 && (
                <Box>
                  <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    COMPLETED ACTION ITEMS:
                  </Heading>
                  <VStack align="stretch" spacing={2}>
                    {completedActionItems.map((actionItem) => (
                      <HStack key={actionItem.id} spacing={2}>
                        <Icon as={CheckCircle} w={4} h={4} color="green.500" />
                        <Text fontSize="sm" color="green.600" textDecoration="line-through">
                          {actionItem.description} {actionItem.dueDate && formatDate(actionItem.dueDate)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* OPEN ISSUES */}
              {openIssues.length > 0 && (
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                      OPEN ISSUES:
                    </Heading>
                    {isPresenter && onAddIssue && (
                      <Button
                        size="xs"
                        leftIcon={<Icon as={Plus} w={3} h={3} />}
                        onClick={() => onAddIssue(rock.id)}
                        variant="ghost"
                      >
                        Add Issue
                      </Button>
                    )}
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {openIssues.map((issue) => (
                      <HStack key={issue.id} spacing={2} justify="space-between">
                        <HStack spacing={2}>
                          <Text fontSize="sm">•</Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {issue.name}
                          </Text>
                        </HStack>
                        {isPresenter && onResolveIssue && (
                          <Button
                            size="xs"
                            colorScheme="green"
                            leftIcon={<Icon as={CheckCircle} w={3} h={3} />}
                            onClick={() => onResolveIssue(issue.id, issue.name)}
                          >
                            Resolve
                          </Button>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* RESOLVED ISSUES */}
              {resolvedIssues.length > 0 && (
                <Accordion allowToggle defaultIndex={[]}>
                  <AccordionItem border="none">
                    <AccordionButton px={0} py={2}>
                      <Heading size="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide" flex={1} textAlign="left">
                        RESOLVED ISSUES:
                      </Heading>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pb={2}>
                      <VStack align="stretch" spacing={2}>
                        {resolvedIssues.map((issue) => (
                          <HStack key={issue.id} spacing={2}>
                            <Icon as={CheckCircle} w={4} h={4} color="green.500" />
                            <Text fontSize="sm" color="green.600" textDecoration="line-through">
                              {issue.name}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}

              {/* No Issues Message */}
              {openIssues.length === 0 && resolvedIssues.length === 0 && (
                <Box>
                  {isPresenter && onAddIssue && (
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        No issues linked to this rock
                      </Text>
                      <Button
                        size="xs"
                        leftIcon={<Icon as={Plus} w={3} h={3} />}
                        onClick={() => onAddIssue(rock.id)}
                        variant="ghost"
                      >
                        Add Issue
                      </Button>
                    </HStack>
                  )}
                  {(!isPresenter || !onAddIssue) && (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      No issues linked to this rock
                    </Text>
                  )}
                </Box>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

